import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/index';
import Pagination from '../paginnation';
import { apiGetPage } from '../../request/api'
import { Spin } from 'antd';
import NotHaveInfo from '../nothaveinfo';
import { useCustomData } from '../../pages/context/mineCustomDataProvider';

interface WorkOrderProps {
    handleViewChange: (newView: string, data?: any) => void;
    data?: any;
}

interface WorkData {
    chooseCommodity: string;
    createdAt: string;
    processingTime: string | null;
    workStatus: string;
}

interface CommodityData {
    id: string;
    commodityName: string;
    menberPrice: string;
}

interface CombinedData {
    workName: string;
    price: string;
    status: string;
    createdAt: string;
    updatedAt: string | null;
}

const WorkOrder: React.FC<WorkOrderProps> = ({ handleViewChange, data }) => {
    const themeColor = useSelector((state: RootState) => state.themeColor.value);
    const [combinedList, setCombinedList] = useState<CombinedData[]>([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const languageCode = localStorage.getItem('siteLanguage') || 'zh';

    const { getValue } = useCustomData();
    const serviceName = getValue('serviceName');
    const price = getValue('price');
    const contactTime = getValue('contactTime');
    const handling = getValue('handling');
    const state = getValue('state');

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            const userId = localStorage.getItem('userId')

            try {
                // 1. 拉取商品信息
                const commodityRes = await apiGetPage({
                    tid: '72D99CCB9A7E439184BF9EF7CABC49BA',
                    page: 1,
                    size: 100,
                    search: '',
                    languagecode: languageCode,
                });

                const commodityMap = new Map<string, CommodityData>();
                (commodityRes.data?.data?.rows || []).forEach((item: CommodityData) => {
                    commodityMap.set(item.id, item);
                });

                // 2. 拉取工单信息
                const workRes = await apiGetPage({
                    tid: 'DFB8D3686DDD4CE88ED9E7659257B8B4',
                    page: 1,
                    size: 1000,
                    languagecode: languageCode,
                    sort: 'workStatus-,',
                    search: 'chooseUser:' + userId
                });

                const workRows: WorkData[] = workRes.data?.data?.rows || [];

                // 3. 合并数据
                const merged = workRows.map(item => {
                    const product = commodityMap.get(item.chooseCommodity);
                    return {
                        workName: product?.commodityName || '未知服务',
                        price: product?.menberPrice || '-',
                        status: item.workStatus,
                        createdAt: item.createdAt,
                        updatedAt: item.processingTime,
                    };
                });

                // 4. 排序逻辑
                const statusPriority: Record<string, number> = {
                    '待处理': 1,
                    '已处理': 2,
                };

                const sorted = merged.sort((a, b) => {
                    const priorityA = statusPriority[a.status] ?? 99;
                    const priorityB = statusPriority[b.status] ?? 99;

                    if (priorityA !== priorityB) {
                        return priorityA - priorityB; // 按状态优先级
                    }
                    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(); // 同状态 → 时间升序
                });

                setCombinedList(sorted);
            } catch (err) {
                console.error('数据加载失败：', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const formatDate = (dateString: string | null) => {
        if (!dateString) return '- -';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '日期格式错误';
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}.${month}.${day}`;
    };

    const totalItems = combinedList.length;
    const paginatedList = combinedList.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    return (
        <Spin spinning={loading}>
            <div className="WorkOrder">
                <div className="work-title">
                    <span style={{ width: '240px' }}>{serviceName}</span>
                    <span style={{ width: '80px' }}>{price}</span>
                    <span style={{ width: '96px' }}>{contactTime}</span>
                    <span style={{ width: '96px' }}>{handling}</span>
                    <span style={{ width: '56px' }}>{state}</span>
                </div>

                {paginatedList.map((item, index) => {
                    const statusText = (() => {
                        if (languageCode === 'zh') {
                            return item.status; // 接口本来就是中文
                        } else {
                            if (item.status === '待处理') return 'Pending';
                            if (item.status === '已处理') return 'Processed';
                            return item.status; // 兜底
                        }
                    })();
                    return (
                        <div key={index} className="order-item">
                            <div className="order-name">{item.workName}</div>
                            <div className="order-price">{item.price}</div>
                            <div className="order-time">{formatDate(item.createdAt)}</div>
                            <div className="order-takeTime">
                                {item.status === '已处理' ? formatDate(item.updatedAt) : '- -'}
                            </div>
                            <div
                                className="order-states"
                                style={item.status === '待处理' ? { color: themeColor } : {}}
                            >
                                {statusText}
                            </div>
                        </div>
                    );
                })}

                {totalItems > itemsPerPage && (
                    <Pagination
                        totalItems={totalItems}
                        itemsPerPage={itemsPerPage}
                        currentPage={currentPage}
                        onPageChange={setCurrentPage}
                        themeColor={themeColor}
                    />
                )}

                {!loading && totalItems === 0 && <NotHaveInfo />}
            </div>
        </Spin>
    );
};

export default WorkOrder;
